// Entry point for the build script in your package.json
import "./mobayilo_auth"
import "trix"
import "@rails/actiontext"
import "@37signals/lexxy"
import "@rails/activestorage"

const initializeLandingMenu = () => {
  const nav = document.querySelector("[data-landing-nav]")
  if (!nav || nav.dataset.menuBound === "true") return

  const toggle = nav.querySelector("[data-landing-menu-toggle]")
  const menu = nav.querySelector("[data-landing-menu]")
  if (!toggle || !menu) return

  nav.dataset.menuBound = "true"

  const closeMenu = () => {
    menu.classList.add("hidden")
    toggle.setAttribute("aria-expanded", "false")
  }

  toggle.addEventListener("click", () => {
    const expanded = toggle.getAttribute("aria-expanded") === "true"
    toggle.setAttribute("aria-expanded", expanded ? "false" : "true")
    menu.classList.toggle("hidden", expanded)
  })

  menu.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      if (window.matchMedia("(max-width: 767px)").matches) closeMenu()
    })
  })

  window.addEventListener("resize", () => {
    if (window.matchMedia("(min-width: 768px)").matches) closeMenu()
  })
}

const inferUrlSourceType = (urlValue) => {
  const url = (urlValue || "").trim().toLowerCase()
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube"
  return "podcast"
}

const initializeLandingIngestion = () => {
  const cards = document.querySelectorAll("[data-landing-ingest]")
  if (cards.length === 0) return

  cards.forEach((card) => {
    if (card.dataset.ingestBound === "true") return
    card.dataset.ingestBound = "true"

    const tabButtons = card.querySelectorAll("[data-ingest-tab-button]")
    const panels = card.querySelectorAll("[data-ingest-panel]")
    const sourceTypeInput = card.querySelector("[data-ingest-source-type]")
    const uploadInput = card.querySelector("[data-ingest-upload-input]")
    const uploadSubmit = card.querySelector("[data-ingest-upload-submit]")
    const uploadFilename = card.querySelector("[data-ingest-upload-filename]")
    const stagedSourceAudioSignedIdInput = card.querySelector("[data-ingest-staged-source-audio-signed-id]")
    const urlInput = card.querySelector("[data-ingest-url-input]")
    const urlSubmit = card.querySelector("[data-ingest-url-submit]")
    const dropzone = card.querySelector("[data-ingest-dropzone]")
    const ingestForm = card.querySelector("[data-ingest-form]")
    const isSignedIn = card.dataset.ingestSignedIn === "true"
    const startAllowed = card.dataset.ingestStartAllowed !== "false"
    const initialTab = card.dataset.ingestInitialTab === "url" ? "url" : "upload"
    const pendingIngestUrl = card.dataset.ingestPendingUrl
    const signInUrl = card.dataset.ingestSignInUrl
    const tabActiveClasses = ["text-slate-950", "border-slate-950"]
    const tabInactiveClasses = ["text-slate-500", "border-transparent"]
    const dropzoneDraggingClasses = ["border-sky-400", "bg-sky-50", "scale-[1.005]"]
    let isPersistingPendingIngest = false
    let activeTab = "upload"

    const setSourceTypeFromUrl = () => {
      if (!sourceTypeInput || !urlInput) return
      sourceTypeInput.value = inferUrlSourceType(urlInput.value)
    }

    const updateUploadState = () => {
      if (!uploadInput || !uploadSubmit) return

      const hasSelectedFile = Boolean(uploadInput.files && uploadInput.files.length > 0)
      const hasStagedFile = Boolean(stagedSourceAudioSignedIdInput?.value?.trim())
      const hasFile = hasSelectedFile || hasStagedFile
      const pendingFilename = uploadFilename?.dataset.ingestPendingUploadFilename || ""
      const fileName = hasSelectedFile ? uploadInput.files[0].name : (hasStagedFile ? pendingFilename : "")

      if (uploadFilename) uploadFilename.textContent = fileName

      const shouldShow = startAllowed && activeTab === "upload" && hasFile
      uploadSubmit.classList.toggle("hidden", !shouldShow)
      uploadSubmit.disabled = !shouldShow
    }

    const updateUrlState = () => {
      if (!urlInput || !urlSubmit) return

      const hasUrl = urlInput.value.trim().length > 0
      const shouldShow = startAllowed && activeTab === "url" && hasUrl
      urlSubmit.classList.toggle("hidden", !shouldShow)
      urlSubmit.disabled = !shouldShow
      setSourceTypeFromUrl()
    }

    const setActiveTab = (tab) => {
      activeTab = tab === "url" ? "url" : "upload"

      tabButtons.forEach((button) => {
        const buttonTab = button.dataset.ingestTabButton
        const isActive = buttonTab === activeTab
        button.setAttribute("aria-selected", isActive ? "true" : "false")
        tabActiveClasses.forEach((className) => {
          button.classList.toggle(className, isActive)
        })
        tabInactiveClasses.forEach((className) => {
          button.classList.toggle(className, !isActive)
        })
      })

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.ingestPanel !== activeTab
      })

      if (uploadInput) uploadInput.disabled = !startAllowed || activeTab !== "upload"
      if (urlInput) urlInput.disabled = !startAllowed || activeTab !== "url"

      if (sourceTypeInput) {
        if (activeTab === "upload") {
          sourceTypeInput.value = "upload"
        } else {
          setSourceTypeFromUrl()
        }
      }

      updateUploadState()
      updateUrlState()
    }

    const persistPendingIngestAndRedirectToSignIn = async () => {
      if (!ingestForm || isPersistingPendingIngest) return
      if (!pendingIngestUrl || !signInUrl) {
        window.location.assign(signInUrl || ingestForm.action)
        return
      }

      const formData = new FormData()
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")

      if (activeTab === "upload") {
        if (!uploadInput?.files?.length) return
        formData.append("episode[source_type]", "upload")
        formData.append("episode[source_audio]", uploadInput.files[0])
      } else {
        const sourceUrl = urlInput?.value?.trim() || ""
        if (!sourceUrl) return
        formData.append("episode[source_type]", inferUrlSourceType(sourceUrl))
        formData.append("episode[source_url]", sourceUrl)
      }

      isPersistingPendingIngest = true
      uploadSubmit?.setAttribute("disabled", "disabled")
      urlSubmit?.setAttribute("disabled", "disabled")

      try {
        const response = await fetch(pendingIngestUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
          },
          credentials: "same-origin",
          body: formData
        })

        if (!response.ok) throw new Error("Failed to persist pending ingest")
        window.location.assign(signInUrl)
      } catch (_error) {
        isPersistingPendingIngest = false
        updateUploadState()
        updateUrlState()
      }
    }

    tabButtons.forEach((button) => {
      button.addEventListener("click", () => {
        setActiveTab(button.dataset.ingestTabButton)
      })
    })

    uploadInput?.addEventListener("change", () => {
      if (uploadInput.files && uploadInput.files.length > 0 && stagedSourceAudioSignedIdInput) {
        stagedSourceAudioSignedIdInput.value = ""
      }
      if (sourceTypeInput) sourceTypeInput.value = "upload"
      updateUploadState()
    })

    urlInput?.addEventListener("input", updateUrlState)

    if (ingestForm && !isSignedIn) {
      ingestForm.addEventListener("submit", (event) => {
        event.preventDefault()
        persistPendingIngestAndRedirectToSignIn()
      })
    } else if (ingestForm && !startAllowed) {
      ingestForm.addEventListener("submit", (event) => {
        event.preventDefault()
      })
    }

    if (dropzone && uploadInput) {
      const dragEnterEvents = ["dragenter", "dragover"]
      dragEnterEvents.forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault()
          dropzoneDraggingClasses.forEach((className) => dropzone.classList.add(className))
        })
      })

      const dragExitEvents = ["dragleave", "drop"]
      dragExitEvents.forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault()
          dropzoneDraggingClasses.forEach((className) => dropzone.classList.remove(className))
        })
      })

      dropzone.addEventListener("drop", (event) => {
        const files = event.dataTransfer?.files
        if (!files || files.length === 0) return

        if (typeof DataTransfer !== "undefined") {
          const transfer = new DataTransfer()
          Array.from(files).forEach((file) => transfer.items.add(file))
          uploadInput.files = transfer.files
        }

        uploadInput.dispatchEvent(new Event("change", { bubbles: true }))
      })
    }

    setActiveTab(initialTab)
  })
}

const recordingSupported = () => {
  return typeof window.MediaRecorder !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function"
}

const formatDatetimeLocalValue = (date) => {
  const pad = (value) => value.toString().padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const recordingFileExtension = (mimeType) => {
  if ((mimeType || "").includes("mp4")) return "m4a"
  if ((mimeType || "").includes("ogg")) return "ogg"
  return "webm"
}

const initializeRecordingWidgets = () => {
  const widgets = document.querySelectorAll("[data-recording-widget]")
  if (widgets.length === 0) return

  widgets.forEach((widget) => {
    if (widget.dataset.recordingBound === "true") return
    widget.dataset.recordingBound = "true"

    const form = widget.closest("form")
    const startButton = widget.querySelector("[data-recording-start]")
    const stopButton = widget.querySelector("[data-recording-stop]")
    const discardButton = widget.querySelector("[data-recording-discard]")
    const statusNode = widget.querySelector("[data-recording-status]")
    const preview = widget.querySelector("[data-recording-preview]")
    const fileInput = form?.querySelector("[data-recording-file-input]")
    const recordedAtInput = form?.querySelector("[data-recording-recorded-at-input]")
    if (!form || !startButton || !stopButton || !discardButton || !statusNode || !preview || !fileInput) return

    let mediaRecorder = null
    let mediaStream = null
    let chunks = []
    let recordingStartedAt = null
    let previewUrl = null
    const recordingAllowed = !startButton.disabled

    const setStatus = (message) => {
      statusNode.textContent = message
    }

    const setButtons = ({ startDisabled, stopDisabled, discardDisabled }) => {
      startButton.disabled = startDisabled
      stopButton.disabled = stopDisabled
      discardButton.disabled = discardDisabled
    }

    const stopTracks = () => {
      if (!mediaStream) return
      mediaStream.getTracks().forEach((track) => track.stop())
      mediaStream = null
    }

    const clearPreview = () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        previewUrl = null
      }
      preview.removeAttribute("src")
      preview.hidden = true
    }

    const clearAttachedFile = () => {
      fileInput.value = ""
      if (typeof DataTransfer !== "undefined") {
        const transfer = new DataTransfer()
        fileInput.files = transfer.files
      }
    }

    const setRecordedAtIfEmpty = () => {
      if (!recordedAtInput || recordedAtInput.value || !recordingStartedAt) return
      recordedAtInput.value = formatDatetimeLocalValue(recordingStartedAt)
    }

    const attachRecordedBlob = (blob) => {
      if (!(blob instanceof Blob) || blob.size === 0) return false
      if (typeof DataTransfer === "undefined") {
        setStatus("Recording captured, but this browser can't attach it automatically. Please upload manually.")
        return false
      }

      const mimeType = blob.type || "audio/webm"
      const filename = `recording-${new Date().toISOString().replace(/[:.]/g, "-")}.${recordingFileExtension(mimeType)}`
      const file = new File([blob], filename, { type: mimeType, lastModified: Date.now() })
      const transfer = new DataTransfer()
      transfer.items.add(file)
      fileInput.files = transfer.files
      return true
    }

    const finalizeRecording = () => {
      const blobType = mediaRecorder?.mimeType || "audio/webm"
      const blob = new Blob(chunks, { type: blobType })
      chunks = []

      if (!attachRecordedBlob(blob)) {
        clearPreview()
        setButtons({ startDisabled: false, stopDisabled: true, discardDisabled: true })
        return
      }

      clearPreview()
      previewUrl = URL.createObjectURL(blob)
      preview.src = previewUrl
      preview.hidden = false
      setRecordedAtIfEmpty()
      setStatus("Recording ready. Click Create Episode to upload.")
      setButtons({ startDisabled: false, stopDisabled: true, discardDisabled: false })
    }

    const startRecording = async () => {
      if (!recordingSupported()) {
        setStatus("Recording is not supported in this browser. Please upload an audio file instead.")
        setButtons({ startDisabled: true, stopDisabled: true, discardDisabled: true })
        return
      }

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (_error) {
        setStatus("Microphone permission denied. Allow microphone access and try again.")
        return
      }

      const preferredMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus"
      ]
      let mimeType = ""
      if (typeof MediaRecorder.isTypeSupported === "function") {
        mimeType = preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || ""
      }

      mediaRecorder = mimeType ? new MediaRecorder(mediaStream, { mimeType }) : new MediaRecorder(mediaStream)
      chunks = []
      recordingStartedAt = new Date()
      clearAttachedFile()
      clearPreview()
      setStatus("Recording in progress...")
      setButtons({ startDisabled: true, stopDisabled: false, discardDisabled: true })

      mediaRecorder.addEventListener("dataavailable", (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data)
      })

      mediaRecorder.addEventListener("stop", () => {
        finalizeRecording()
        stopTracks()
        mediaRecorder = null
      }, { once: true })

      mediaRecorder.start()
    }

    if (!recordingAllowed) {
      setStatus("Top up credits to enable in-app recording.")
      setButtons({ startDisabled: true, stopDisabled: true, discardDisabled: true })
      return
    }

    startButton.addEventListener("click", () => {
      if (startButton.disabled) return
      startRecording()
    })

    stopButton.addEventListener("click", () => {
      if (!mediaRecorder || mediaRecorder.state !== "recording") return
      setStatus("Finalizing recording...")
      stopButton.disabled = true
      mediaRecorder.stop()
    })

    discardButton.addEventListener("click", () => {
      if (mediaRecorder && mediaRecorder.state === "recording") return
      clearAttachedFile()
      clearPreview()
      setStatus("Recording discarded. Ready to record again.")
      setButtons({ startDisabled: false, stopDisabled: true, discardDisabled: true })
    })

    form.addEventListener("submit", (event) => {
      if (mediaRecorder && mediaRecorder.state === "recording") {
        event.preventDefault()
        setStatus("Stop recording before creating the episode.")
        return
      }

      if (!fileInput.files || fileInput.files.length === 0) {
        event.preventDefault()
        setStatus("Record audio first, then create the episode.")
      }
    })

    if (!recordingSupported()) {
      setStatus("Recording is not supported in this browser. Please upload an audio file instead.")
      setButtons({ startDisabled: true, stopDisabled: true, discardDisabled: true })
    } else {
      setButtons({ startDisabled: false, stopDisabled: true, discardDisabled: true })
    }

    window.addEventListener("beforeunload", stopTracks)
  })
}

const toggleEpisodeSourceFields = () => {
  const sourceTypeSelect = document.querySelector("#episode_source_type")
  if (!sourceTypeSelect) return

  const uploadField = document.querySelector('[data-source-field="upload"]')
  const urlField = document.querySelector('[data-source-field="url"]')
  const textField = document.querySelector('[data-source-field="text"]')
  const sourceAudioInput = document.querySelector("#episode_source_audio")
  const sourceUrlInput = document.querySelector("#episode_source_url")
  const sourceType = sourceTypeSelect.value
  const uploadSelected = sourceType === "upload"
  const textSelected = sourceType === "text"

  if (uploadField) uploadField.hidden = !uploadSelected
  if (urlField) urlField.hidden = uploadSelected || textSelected
  if (textField) textField.hidden = !textSelected
  if (sourceAudioInput) sourceAudioInput.required = uploadSelected
  if (sourceUrlInput) sourceUrlInput.required = !uploadSelected && !textSelected

  sourceTypeSelect.onchange = toggleEpisodeSourceFields
}

const copyToClipboard = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text)
    return true
  }

  const fallback = document.createElement("textarea")
  fallback.value = text
  fallback.setAttribute("readonly", "")
  fallback.style.position = "absolute"
  fallback.style.left = "-9999px"
  document.body.appendChild(fallback)
  fallback.select()
  const success = document.execCommand("copy")
  document.body.removeChild(fallback)
  return success
}

const initializeCopyButtons = () => {
  const buttons = document.querySelectorAll("[data-copy-source-id]")
  if (buttons.length === 0) return

  buttons.forEach((button) => {
    if (button.dataset.copyInitialized === "true") return
    button.dataset.copyInitialized = "true"

    button.addEventListener("click", async () => {
      const sourceElement = document.getElementById(button.dataset.copySourceId)
      if (!sourceElement) return

      const feedbackId = button.dataset.copyFeedbackId
      const feedback = feedbackId ? document.getElementById(feedbackId) : null

      try {
        await copyToClipboard(sourceElement.innerText)
        if (feedback) feedback.textContent = "Copied."
      } catch (_error) {
        if (feedback) feedback.textContent = "Copy failed. Select text manually."
      }
    })
  })
}

const initializeFlashMessages = () => {
  const flashes = document.querySelectorAll(".flash")
  if (flashes.length === 0) return

  flashes.forEach((flash) => {
    if (flash.dataset.dismissInitialized === "true") return
    flash.dataset.dismissInitialized = "true"

    window.setTimeout(() => {
      flash.remove()
    }, 3000)
  })
}

document.addEventListener("DOMContentLoaded", toggleEpisodeSourceFields)
document.addEventListener("turbo:load", toggleEpisodeSourceFields)
document.addEventListener("DOMContentLoaded", initializeCopyButtons)
document.addEventListener("turbo:load", initializeCopyButtons)
document.addEventListener("DOMContentLoaded", initializeLandingMenu)
document.addEventListener("turbo:load", initializeLandingMenu)
document.addEventListener("DOMContentLoaded", initializeLandingIngestion)
document.addEventListener("turbo:load", initializeLandingIngestion)
document.addEventListener("DOMContentLoaded", initializeRecordingWidgets)
document.addEventListener("turbo:load", initializeRecordingWidgets)
document.addEventListener("DOMContentLoaded", initializeFlashMessages)
document.addEventListener("turbo:load", initializeFlashMessages)

// Stimulus setup
import { Application } from "@hotwired/stimulus"
import TextEditorController from "./controllers/text_editor_controller"
import ConfirmController from "./controllers/confirm_controller"
import PanelLayoutController from "./controllers/panel_layout_controller"
import TrailerRecorderController from "./controllers/trailer_recorder_controller"
import EpisodeUploadController from "./controllers/episode_upload_controller"
import CelebrateController from "./controllers/celebrate_controller"

const stimulusApp = Application.start()
stimulusApp.register("text-editor", TextEditorController)
stimulusApp.register("confirm", ConfirmController)
stimulusApp.register("panel-layout", PanelLayoutController)
stimulusApp.register("trailer-recorder", TrailerRecorderController)
stimulusApp.register("episode-upload", EpisodeUploadController)
stimulusApp.register("celebrate", CelebrateController)
